/**
 * The left menu's three ways (MB's R10, as Supabase's sidebar): pinned (stored as `expanded`), icons only (the default,
 * MB 25 Oct 2026), or hidden. The choice is the person's, kept in this browser under their sign-in (`menuModeKey`).
 * Teslyn Field has the same control (apps/field/src/components/MenuMode.tsx).
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { MENU_MODES, menuModeKey, parseMenuMode, type MenuMode } from '@teslyn/domain';
import { Icon } from './Icon.tsx';

function readMode(key: string): MenuMode {
  try {
    return parseMenuMode(window.localStorage.getItem(key));
  } catch {
    return parseMenuMode(null);
  }
}

/** The person's menu choice and its setter; follows a change of person (signing in or out). */
export function useMenuMode(personId: string | null): readonly [MenuMode, (m: MenuMode) => void] {
  const key = menuModeKey('cloud', personId);
  const [state, setState] = useState(() => ({ key, mode: readMode(key) }));
  // another person signed in: their own choice (adjusting state while rendering)
  if (state.key !== key) setState({ key, mode: readMode(key) });
  function set(mode: MenuMode): void {
    setState({ key, mode });
    try {
      window.localStorage.setItem(key, mode);
    } catch {
      // storage blocked: the choice holds until the page is closed
    }
  }
  return [state.key === key ? state.mode : readMode(key), set] as const;
}

/** The choices beside the button, kept on the screen: upwards from its foot, or downwards near the top. */
function besideButton(r: DOMRect): CSSProperties {
  const room = 200;
  const left = Math.max(0, Math.min(r.right, window.innerWidth - room));
  return r.bottom > room ? { left, bottom: window.innerHeight - r.bottom } : { left, top: r.bottom };
}

const label = (m: MenuMode): string => MENU_MODES.find((x) => x.value === m)!.label;

/** The menu's own control at its foot: "Menu: Icons only" (or "Menu: Pinned", "Menu: Hidden"), opening the three choices. */
export function MenuModeControl({
  mode,
  onChange,
  onOpenChange,
}: {
  readonly mode: MenuMode;
  readonly onChange: (m: MenuMode) => void;
  /** The choices opened or closed: the menu stays open under them (icons only, or hidden opened for a moment). */
  readonly onOpenChange?: (open: boolean) => void;
}): ReactElement {
  // where the choices open: beside the button, over the page (the menu itself scrolls, so they cannot sit in it)
  const [open, setOpen] = useState<CSSProperties | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const isOpen = open !== null;
  const told = useRef(onOpenChange);
  useEffect(() => {
    told.current = onOpenChange;
  });
  useEffect(() => told.current?.(isOpen), [isOpen]);
  // a click elsewhere or Escape closes the choices
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(null);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const name = `Menu: ${label(mode)}`;
  return (
    <div className="menu-mode" ref={box}>
      <button
        type="button"
        className="sidenav__item menu-mode__button"
        aria-haspopup="menu"
        aria-expanded={open !== null}
        aria-label={name}
        title={name}
        onClick={(e) => {
          if (open) return setOpen(null);
          setOpen(besideButton(e.currentTarget.getBoundingClientRect()));
        }}
      >
        <Icon name="panel-left" />
        <span className="sidenav__label">{name}</span>
      </button>
      {open && (
        <div className="menu-mode__list" role="menu" aria-label="Left menu" style={open}>
          {MENU_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              role="menuitemradio"
              aria-checked={m.value === mode}
              className={m.value === mode ? 'menu-mode__choice menu-mode__choice--on' : 'menu-mode__choice'}
              onClick={() => {
                setOpen(null);
                onChange(m.value);
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
